import { useCallback, useEffect, useRef, useState } from "react";

import { CSV_PAGE_SIZE, normalizeCsvLine } from "./csvReviewFormat";
import { REVIEWABLE_TASK_STATUSES } from "./reviewLineGates";
import {
  fetchCsvAttentionGroups,
  fetchCsvReviewSummary,
  listCsvLines,
  subscribeToBulkCommitRun
} from "../../../helpers/smartUpload";

// csv-review-summary also answers while the file is still being read and
// once the batch is finished; attention groups and every edit only while the
// batch is open for review.
export const CSV_READABLE_TASK_STATUSES = [
  ...REVIEWABLE_TASK_STATUSES,
  "running",
  "finished"
];

export const ACTIVE_RUN_STATUSES = ["tagging", "running"];
export const TERMINAL_RUN_STATUSES = [
  "completed",
  "cancelled",
  "failed",
  "stalled"
];

const EMPTY_GROUPS = { groups: [], truncated: false };

// Counts and attention groups for a migrated CSV batch, re-read after every
// action. A response that arrives after a newer request started is dropped,
// so a slow read never overwrites fresher counts.
export const useCsvReviewSummary = ({ companyId, taskDocId, taskStatus }) => {
  const [summary, setSummary] = useState(null);
  const [attention, setAttention] = useState(EMPTY_GROUPS);
  const [loadError, setLoadError] = useState(null);
  const [attentionError, setAttentionError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const latestRequestRef = useRef(0);

  const canRead = CSV_READABLE_TASK_STATUSES.includes(taskStatus);
  const canReview = REVIEWABLE_TASK_STATUSES.includes(taskStatus);

  const refresh = useCallback(async () => {
    if (!canRead) {
      return null;
    }
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    const [summaryResponse, groupsResponse] = await Promise.all([
      fetchCsvReviewSummary({ companyId, taskDocId }),
      canReview
        ? fetchCsvAttentionGroups({ companyId, taskDocId })
        : Promise.resolve(EMPTY_GROUPS)
    ]);
    if (requestId !== latestRequestRef.current) {
      return null;
    }
    setRefreshKey((current) => current + 1);
    if (!summaryResponse || summaryResponse.error) {
      return setLoadError(
        summaryResponse?.message ||
          "We couldn't load this file's review — please try again."
      );
    }
    setLoadError(null);
    setSummary(summaryResponse);
    if (!groupsResponse || groupsResponse.error) {
      return setAttentionError(
        groupsResponse?.message ||
          "We couldn't load the lines that need your attention."
      );
    }
    setAttentionError(null);
    return setAttention(groupsResponse);
  }, [companyId, taskDocId, canRead, canReview]);

  useEffect(() => {
    refresh();
  }, [refresh, taskStatus]);

  return {
    summary,
    attention,
    attentionError,
    loadError,
    refresh,
    refreshKey,
    canReview
  };
};

// One page of lines at a time (≤50), keyed on the server's source_row_number
// cursor. The pager is disabled while a page loads, and a page answered after
// the filter changed is dropped.
export const useCsvLinePages = ({
  companyId,
  taskDocId,
  filter,
  groupKey,
  isEnabled = true,
  refreshKey
}) => {
  const [page, setPage] = useState({
    lines: [],
    cursor: null,
    previousCursors: [],
    nextCursor: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const latestRequestRef = useRef(0);

  const loadPage = useCallback(
    async ({ cursor, previousCursors }) => {
      const requestId = latestRequestRef.current + 1;
      latestRequestRef.current = requestId;
      setIsLoading(true);
      const response = await listCsvLines({
        companyId,
        taskDocId,
        filter,
        groupKey,
        cursor,
        pageSize: CSV_PAGE_SIZE
      });
      if (requestId !== latestRequestRef.current) {
        return null;
      }
      setIsLoading(false);
      if (!response || response.error || !Array.isArray(response.lines)) {
        return setError(
          response?.message ||
            "We couldn't load these lines — please try again."
        );
      }
      setError(null);
      return setPage({
        lines: response.lines.map(normalizeCsvLine),
        cursor,
        previousCursors,
        nextCursor: response.nextCursor ?? null
      });
    },
    [companyId, taskDocId, filter, groupKey]
  );

  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    loadPage({ cursor: null, previousCursors: [] });
  }, [isEnabled, loadPage, refreshKey]);

  const { cursor, previousCursors, nextCursor } = page;

  return {
    lines: page.lines,
    isLoading,
    error,
    pageNumber: previousCursors.length + 1,
    hasNext: nextCursor !== null,
    hasPrevious: !!previousCursors.length,
    goNext: () =>
      loadPage({
        cursor: nextCursor,
        previousCursors: [...previousCursors, cursor]
      }),
    goPrevious: () =>
      loadPage({
        cursor: previousCursors[previousCursors.length - 1],
        previousCursors: previousCursors.slice(0, -1)
      })
  };
};

const RUN_MISSING_COPY =
  "We couldn't find this confirmation — refresh to see where it stands.";
const RUN_LISTENER_ERROR_COPY =
  "We lost track of this confirmation's progress — it keeps running; reopen this window to check on it.";

// The one listener CSV mode holds besides the task: the active run's doc.
export const useBulkCommitRun = ({ companyId, taskDocId, runId }) => {
  const [run, setRun] = useState(null);
  const [runError, setRunError] = useState(null);

  useEffect(() => {
    setRun(null);
    setRunError(null);
    if (!companyId || !taskDocId || !runId) {
      return undefined;
    }
    return subscribeToBulkCommitRun({
      companyId,
      taskDocId,
      runId,
      onUpdate: (nextRun) => {
        setRunError(null);
        return setRun(nextRun);
      },
      onMissing: () => {
        setRun(null);
        return setRunError(RUN_MISSING_COPY);
      },
      onError: () => setRunError(RUN_LISTENER_ERROR_COPY)
    });
  }, [companyId, taskDocId, runId]);

  return { run, runError };
};
